/**
 * Formats a currency string.
 *
 * @param input - The input string (e.g., "USD1000").
 * @returns The formatted currency string (e.g., "$ 1,000").
 *
 * @example
 * formatCurrency("USD1000"); // "$ 1,000"
 * formatCurrency("JPY4000"); // "JPY 4,000"
 */
export const formatPrice = (input: string) => {
  // Match strings like "USD1000" or "EUR1000"
  const match = input.match(/^([a-zA-Z]+)(\d+)$/);

  if (!match) return input;

  const [, currencyCode, amount] = match;

  // Format the amount with commas for thousands
  const formattedAmount = parseInt(amount, 10).toLocaleString();

  const currencySymbols: { [key: string]: string } = {
    USD: '$',
    EUR: '€',
    GBP: '£',
  };

  const currencySymbol =
    currencySymbols[currencyCode.toUpperCase()] || currencyCode.toUpperCase();
  return `${currencySymbol} ${formattedAmount}`;
};

interface PriceFormatData {
  price: number;
  currency?: string;
  locale?: string;
}

export function formatPriceWithCurrency(data: PriceFormatData): string {
  let priceNum: number;
  if (typeof data.price === 'number') {
    priceNum = data.price;
  } else {
    priceNum = Number(data.price);
    if (isNaN(priceNum)) return '';
  }
  const formatted = (() => {
    if (!data.currency) {
      // No currency provided, format as decimal only
      return new Intl.NumberFormat(verifyLocale(data.locale), {
        style: 'decimal',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(priceNum);
    }
    try {
      return new Intl.NumberFormat(verifyLocale(data.locale), {
        style: 'currency',
        currency: data.currency,
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(priceNum);
    } catch {
      // fallback to decimal if currency code is invalid
      const str = new Intl.NumberFormat(verifyLocale(data.locale), {
        style: 'decimal',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(priceNum);
      return str + ' ' + data.currency;
    }
  })();
  return formatted.replace(/\u00a0/g, ' ').trim();
}

function verifyLocale(locale: string | undefined): string | undefined {
  if (!locale) {
    // fallback to browser default locale
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }
    return undefined;
  }
  try {
    return Intl.getCanonicalLocales(locale)[0];
  } catch {
    return undefined;
  }
}
